import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { taskApi } from '../../api/task.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { Btn, ConfirmDialog, Input, PageHeader, Select, Spinner, Textarea } from '../../components/ui/index';
import FileDropZone from '../../components/ui/FileDropZone';
import JalaliDatePicker from '../../components/calendar/JalaliDatePicker';
import { validateFile, getAcceptAttribute, getLimitsHint } from '../../utils/fileValidation';

const pad = (n) => String(n).padStart(2, '0');

/** Manager-only: define a task. Works like a ticket draft - every edit is
 * auto-saved server-side, files upload immediately, "ارسال" publishes it. */
export default function TaskFormPage() {
  const { user } = useAuth();
  const { addToast } = useUI();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [taskId, setTaskId] = useState(null);
  const [ready, setReady] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [day, setDay] = useState(null);
  const [time, setTime] = useState('12:00');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const loaded = useRef(false);

  const deadlineIso = () => {
    if (!day) return '';
    const [h, m] = time.split(':').map(Number);
    const d = new Date(day);
    d.setHours(h || 0, m || 0, 0, 0);
    return d.toISOString();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [asg, draftId] = await Promise.all([
          taskApi.getAssignees(),
          params.get('draft') ? Promise.resolve(Number(params.get('draft'))) : taskApi.createDraft().then((r) => r.data.task_id),
        ]);
        if (cancelled) return;
        setAssignees(asg.data || []);
        setTaskId(draftId);
        const res = await taskApi.get(draftId);
        const t = res.data;
        if (t.status !== 'draft') { navigate(`/tasks/${draftId}`, { replace: true }); return; }
        setTitle(t.title);
        setDescription(t.description);
        setAssigneeId(t.assignee_id || '');
        if (t.deadline) {
          const d = new Date(t.deadline);
          setDay(d);
          setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
        }
        setFiles((t.files_list || []).map((f) => ({ name: f.main_file_name, serverName: f.server_file_name })));
      } catch {
        addToast('خطا در بارگذاری پیش‌نویس', 'error');
      }
      loaded.current = true;
      setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = () => taskApi.updateDraft({
    task_id: taskId, title, description, assignee_id: assigneeId || null, deadline: deadlineIso(),
  });

  // debounce autosave, like the ticket draft
  useEffect(() => {
    if (!loaded.current || !taskId) return undefined;
    const id = setTimeout(() => { saveDraft().catch(() => {}); }, 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, assigneeId, day, time]);

  const handleFileAdd = async (f) => {
    const err = validateFile(f, user?.type, files);
    if (err) { addToast(err, 'error'); return; }
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(f);
    });
    try {
      const res = await taskApi.sendFile({ task_id: taskId, file_name: f.name, url: dataUrl });
      const serverName = res.data?.file_name || f.name;
      setFiles((prev) => [...prev.filter((x) => x.name !== f.name), { name: f.name, serverName }]);
    } catch (e) {
      addToast(typeof e?.response?.data === 'string' ? e.response.data : 'خطا در آپلود فایل', 'error');
    }
  };

  const removeFile = async (file) => {
    try { await taskApi.deleteFile({ task_id: taskId, main_file_name: file.name }); } catch {}
    setFiles((prev) => prev.filter((f) => f.serverName !== file.serverName));
  };

  const send = async () => {
    setSending(true);
    try {
      await saveDraft();
      await taskApi.send(taskId);
      addToast('تسک ارسال شد', 'success');
      navigate('/tasks?scope=created');
    } catch (e) {
      addToast(typeof e?.response?.data === 'string' ? e.response.data : 'خطا در ارسال تسک', 'error');
    }
    setSending(false);
  };

  const discard = async () => {
    try { await taskApi.deleteDraft(taskId); } catch {}
    navigate('/tasks?scope=created');
  };

  if (!ready) return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>;

  const options = [{ value: '', label: 'انتخاب کارمند...' }, ...assignees.map((a) => ({
    value: a.employee_id, label: a.own_department ? a.name : `${a.name} (${a.department_name})`,
  }))];

  return (
    <div className="fade-in" style={{ maxWidth: 720 }}>
      <PageHeader
        title="تسک جدید"
        subtitle="تغییرات به صورت پیش‌نویس ذخیره می‌شود"
        action={<Link to="/tasks?scope=created" style={{ fontSize: 13, color: '#64748b' }}>بازگشت</Link>}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Select label="محول به" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} options={options} />
        {assignees.length <= 1 && (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            برای افزودن کارمندان سایر دپارتمان‌ها به <Link to="/settings/task-members">تنظیمات تسک</Link> بروید.
          </div>
        )}
        <Input label="عنوان" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        <Textarea label="شرح تسک" value={description} maxLength={2000} rows={6} onChange={(e) => setDescription(e.target.value)} />

        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>ددلاین</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <JalaliDatePicker value={day} onChange={setDay} placeholder="انتخاب تاریخ" />
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              style={{ padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>پیوست‌ها</label>
          <FileDropZone onFile={handleFileAdd} accept={getAcceptAttribute(user?.type)} limitsHint={getLimitsHint(user?.type)} />
          {files.map((f) => (
            <div key={f.serverName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginTop: 6, background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <span>📄</span>
              <span style={{ flex: 1, fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <button onClick={() => removeFile(f)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Btn loading={sending} onClick={send}>ارسال تسک</Btn>
          <Btn variant="secondary" onClick={() => setConfirmDelete(true)}>حذف پیش‌نویس</Btn>
        </div>
      </div>
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={discard}
        title="حذف پیش‌نویس" message="این پیش‌نویس و فایل‌های آن حذف می‌شود." danger />
    </div>
  );
}
